<!-- routify:options index=3 -->
<script lang="ts">
  import { t } from 'svelte-i18n';
  import PageSectionContent from '../shared/page-section-content.svelte';
  import PageSectionHeader from '../shared/page-section-header.svelte';
  import IntersectionObserver from 'svelte-intersection-observer';
  import { setHeaderColor } from '../shared/header-store';
  import { onDestroy } from 'svelte';
  import FooterSection from '../components/home/footer-section.svelte';
  import OurValuesList from '../components/about/our-values-list.svelte';
  import { trackViewSection } from '../shared/utils';

  let elementsGroupDownwards: HTMLElement[] = [];
  let elementsGroupUpwards: HTMLElement[] = [];
  let intersectingGroupDownwards: boolean[] = [];
  let intersectingGroupUpwards: boolean[] = [];
  let isObserverDirty: boolean = false;
  let firstSectionHeight: number;
  let offsetWidth: number;

  const scrollDownButtonGap = 62;

  $: showScrollDownButton = screen.height > (firstSectionHeight + scrollDownButtonGap);

  const logos = [
    { alt: 'Angular', src: 'images/logo-angular.png' },
    { alt: 'PHP', src: 'images/logo-php.png' },
    { alt: 'Laravel', src: 'images/logo-laravel.png' },
    { alt: 'Ionic', src: 'images/logo-ionic.png' },
    { alt: 'AWS', src: 'images/logo-aws.png' },
    { alt: 'Capacitor', src: 'images/logo-capacitor.png' },
    { alt: 'NodeJS', src: 'images/logo-nodejs.png' },
    { alt: 'GO', src: 'images/logo-go.png' },
    { alt: 'Svelte', src: 'images/logo-svelte.png' },
    { alt: 'Postgre', src: 'images/logo-postgre.png' },
    { alt: 'WebRTC', src: 'images/logo-webrtc.png' },
    { alt: 'Flutteflow', src: 'images/logo-flutterflow.png' },
  ];

  onDestroy(() => {
    isObserverDirty = false;
  });

  function scrollDown() {      
    const sectionHeightScroll = firstSectionHeight - 10;

    scrollTo({ top: sectionHeightScroll, behavior: 'smooth' })
  }

  function setHeaderTheme(theme: string, background: string) {
    if (!isObserverDirty) {
      isObserverDirty = true;
      return;
    }

    setHeaderColor(theme, background, '/index');
  }
</script>

<IntersectionObserver
  element={elementsGroupUpwards[0]}
  bind:intersecting={intersectingGroupUpwards[0]}
  on:intersect={() => {
    if (intersectingGroupUpwards[0]) {
      setHeaderTheme('white', 'blue');
    }
  }}>
  <IntersectionObserver
    element={elementsGroupDownwards[0]}
    bind:intersecting={intersectingGroupDownwards[0]}
    on:intersect={() => {
      if (intersectingGroupDownwards[0] && !intersectingGroupDownwards[1]) {
        setHeaderTheme('white', 'blue');
        trackViewSection('about-header-section');
      }
    }}>
    <div
      class="relative pt-[72px] md2:pt-[80px] xl:pt-[104px] w-[100%] bg-blue-bg selection:bg-text-dark selection:text-blue"
      bind:offsetHeight={firstSectionHeight}
      bind:offsetWidth={offsetWidth}
    >
      <PageSectionHeader>
        <img
          class="w-100 select-none block"
          alt="Illustration of an astronaut floating"
          src="images/about-illustration.png"
          slot="illustration"
        />
        <div slot="description" class="text-center md2:text-left">
          <h1 class="p-0 pb-6 text-white text-xxs md:text-md leading-[1.3] selection:bg-text-dark selection:text-blue">
            {$t('ABOUT.HEADING')}
          </h1>
          <p class="md2:text-xxs selection:bg-text-dark leading-[1.75] selection:text-blue p-0">
            {$t('ABOUT.SUBHEADING')}
          </p>
        </div>
      </PageSectionHeader>
      <div class="absolute bottom-0 w-full bg-page-fade-shadow">
        <div on:click={scrollDown}  class="cursor-pointer flex flex-col items-center md2:flex-row md2:px-[4.5rem] pb-8 md2:pb-16 xl:pb-[84px] lg:px-40  xl:max-w-[1248px] xl:px-0 xl:mx-auto">
          <p class="text-yellow p-0 text-base md2:text-xs lg:text-xs">
            {$t('ABOUT.LEARN_MORE')}
          </p>
          <ion-icon name="chevron-down-sharp" class="text-yellow text-md mt-2 ml-0 md2:mt-0 md2:ml-2"></ion-icon>
        </div>
      </div>
      <span class="absolute bottom-[76px]" bind:this={elementsGroupUpwards[0]} />
      <span class="absolute top-[calc(100vh-72px)]" bind:this={elementsGroupDownwards[0]} />
    </div>
  </IntersectionObserver>
</IntersectionObserver>

<IntersectionObserver
  element={elementsGroupUpwards[1]}
  bind:intersecting={intersectingGroupUpwards[1]}
  on:intersect={() => {
    if (intersectingGroupUpwards[1] && !intersectingGroupDownwards[0]) {
      setHeaderTheme('black', 'white');
    }
  }}>
  <IntersectionObserver
    threshold={0}
    element={elementsGroupDownwards[1]}
    bind:intersecting={intersectingGroupDownwards[1]}
    on:intersect={() => {
      if (intersectingGroupDownwards[1] && !intersectingGroupDownwards[2]) {
        setHeaderTheme('black', 'white');
        trackViewSection('about-our-values-section');
      }
    }}>
    <div class="bg-white relative">
      <PageSectionContent>
        <div
          slot="text"
          class="text-center md2:text-left"
        >
          <h2 class="p-0 pb-6 text-xxs md:text-md leading-none selection-light">{$t('ABOUT.VALUES.TITLE')}</h2>
          <p class="text-opaque-black leading-[1.75] lg:w-[710px] p-0 selection-light">{$t('ABOUT.VALUES.DESCRIPTION')}</p>
        </div>
      </PageSectionContent>
      <OurValuesList></OurValuesList>

      <span class="absolute bottom-[76px]" bind:this={elementsGroupUpwards[1]} />
      <span class="absolute top-[calc(100vh-72px)]" bind:this={elementsGroupDownwards[1]} />
    </div>
  </IntersectionObserver>
</IntersectionObserver>

<IntersectionObserver
  element={elementsGroupUpwards[2]}
  bind:intersecting={intersectingGroupUpwards[2]}
  on:intersect={() => {
    if (intersectingGroupUpwards[2] && !intersectingGroupDownwards[1] && !intersectingGroupUpwards[1]) {
      setHeaderTheme('black', 'yellow');
    }
  }}>
  <IntersectionObserver
    threshold={0}
    element={elementsGroupDownwards[2]}
    bind:intersecting={intersectingGroupDownwards[2]}
    on:intersect={() => {
      if (intersectingGroupDownwards[2] && !intersectingGroupDownwards[3]) {
        setHeaderTheme('black', 'yellow');
        trackViewSection('about-stand-for-section');
      }
    }}>
    <div class="relative flex justify-center w-full py-16 
    md:py-24 md2:py-32 lg:py-36 bg-yellow-light md2:bg-yellow-decoration bg-no-repeat md2:bg-about-decoration-md2 lg:bg-about-decoration-lg xl:bg-about-decoration-xl">
      <div
        class="l-layout__sides text-center md2:text-left"
      >
        <h2 class="p-0 pb-6 text-xxs md:text-md leading-none selection-light">{$t('ABOUT.STAND_TITLE')}</h2>
        <p class="text-opaque-black leading-[1.75] lg:w-[710px] p-0">{$t('ABOUT.STAND_DESCRIPTION')}</p>
      </div>

      <span class="absolute bottom-[76px]" bind:this={elementsGroupUpwards[2]} />
      <span class="absolute top-[calc(100vh-72px)]" bind:this={elementsGroupDownwards[2]} />
    </div>
  </IntersectionObserver>
</IntersectionObserver>

<IntersectionObserver
  element={elementsGroupUpwards[3]}
  bind:intersecting={intersectingGroupUpwards[3]}
  on:intersect={() => {
    if (intersectingGroupUpwards[3] && !intersectingGroupDownwards[2] && !intersectingGroupDownwards[4]) {
      setHeaderTheme('black', 'white');
    }
  }}>
  <IntersectionObserver
    element={elementsGroupDownwards[3]}
    bind:intersecting={intersectingGroupDownwards[3]}
    on:intersect={() => {
      if (intersectingGroupDownwards[3] && !intersectingGroupDownwards[4]) {
        setHeaderTheme('black', 'white');
        trackViewSection('about-team-section');
      }
    }}>
    <div class="bg-white relative">
      <PageSectionContent>
        <div
          slot="text"
          class="text-center md2:text-left"
        >
          <h2 class="p-0 pb-6 text-xxs md:text-md leading-none selection-light">{$t('ABOUT.TEAM_TITLE')}</h2>
          <p class="text-opaque-black leading-[1.75] lg:w-[710px] p-0">{$t('ABOUT.TEAM_DESCRIPTION')}</p>
        </div>
        <div slot="content" class="flex w-full justify-center items-center md2:justify-start mt-[40px] xs:mt-12 sm:mt-[56px] lg:mt-16 xl:mt-[72px]">   
          <img src={ offsetWidth >= 1024 ? 'images/team-desktop.png' : 'images/team-mobile.png' } alt="team events" class="w-[267px] md2:w-full h-auto xl:max-w-[1249px]">
        </div>
      </PageSectionContent>

      <span class="absolute bottom-[76px]" bind:this={elementsGroupUpwards[3]} />
      <span class="absolute top-[calc(100vh-72px)]" bind:this={elementsGroupDownwards[3]} />
    </div>
  </IntersectionObserver>
</IntersectionObserver>

<IntersectionObserver
  element={elementsGroupUpwards[4]}
  bind:intersecting={intersectingGroupUpwards[4]}
  on:intersect={(e) => {
    if (intersectingGroupUpwards[4] && !intersectingGroupDownwards[3]) {
      setHeaderTheme('white', 'blue');
    }
  }}>
  <IntersectionObserver
    element={elementsGroupDownwards[4]}
    bind:intersecting={intersectingGroupDownwards[4]}
    on:intersect={() => {
      if (intersectingGroupDownwards[4]) {
        setHeaderTheme('white', 'blue');
        trackViewSection('about-tech-section');
      }
    }}>
    <div class="bg-blue-bg relative pb-20">
      <PageSectionContent>
        <div
        slot="text"
        class="text-center md2:text-left xl:flex md2:items-start xl:max-w-[1249px]"
        >
          <h2 class="text-white p-0 pb-6 md2:w-[442px] text-xxs md:text-md leading-[1.30] selection-dark">{$t('ABOUT.TECH_TITLE')}</h2>
          <p class="text-white-90 leading-[1.75] xl:ml-auto lg:w-[710px] p-0 selection-dark">{$t('ABOUT.TECH_DESCRIPTION')}</p>
        </div>

        <div
          slot="content"
          class="justify-around flex flex-wrap xl:max-w-[1249px]
          mt-[40px] xs:mt-12 sm:mt-[56px] lg:mt-16 xl:mt-[72px]
          gap-[8px] sm:gap-[14px] md:gap-[22px] lg:gap-[38px]"
        >
          {#each logos as { alt, src }}
            <div class="select-none w-[calc(50%-8px)] sm:w-[154px] md:w-[166px] md2:w-[128px] xl:w-[173px] flex justify-center items-center opacity-60">
              <img class="w-100 pointer-events-none" {alt} {src} />
            </div>
          {/each}
        </div>
      </PageSectionContent>
      <FooterSection />
      <span class="absolute bottom-[76px]" bind:this={elementsGroupUpwards[4]} />
      <span class="absolute top-[calc(100vh-72px)]" bind:this={elementsGroupDownwards[4]} />
    </div>
  </IntersectionObserver>
</IntersectionObserver>